import Button from "components/Button/Button";
import Card from "components/Card/Card";
import KaboodleToaster from "components/common/KaboodleToaster";
import type { DocumentType } from "components/Documents/DocumentTypes";
import { Select } from "components/inputs";
import type { OptionType } from "components/inputs/Select/Select";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useEffect, useMemo, useState } from "react";
import { useGetDocumentsQuery } from "services/documents-api";
import { useGetServiceDateOfInjuryQuery } from "services/doi-api";
import {
  useExtractPayHistoryMutation,
  useGetSupportedPayHistoryProvidersQuery,
} from "services/future-medical-api";
import type { ApiError } from "types/ApiTypes";
import type { ClaimNumber, ServiceDateOfInjury } from "types/DataTypes";

import { getServiceField } from "../../future-medical-utils";

import { getErrorMessage } from "../medicalRecordUtils";

type ExtractPayHistoryModalService = {
  id?: number;
  type?: string;
  file?: { id?: number; claim_numbers?: ClaimNumber[] } | null;
};
type ExtractPayHistoryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  service: ExtractPayHistoryModalService;
  preselectedDocumentId?: number | null;
};

export const ACCEPTED_PAY_HISTORY_EXTENSIONS = [".csv", ".xlsx", ".xls"];

const formatDoiDate = (value?: string | null) => value?.substring(0, 10) ?? "";

const ExtractPayHistoryModal = ({
  isOpen,
  onClose,
  service,
  preselectedDocumentId,
}: ExtractPayHistoryModalProps) => {
  const [selectedProvider, setSelectedProvider] = useState<OptionType | null>(
    null,
  );
  const [selectedDocId, setSelectedDocId] = useState<number | null>(null);
  const [selectedDoiId, setSelectedDoiId] = useState<number | null>(null);
  const [selectedClaimNumberId, setSelectedClaimNumberId] = useState<
    number | null
  >(null);
  const [isExtracting, setIsExtracting] = useState(false);

  const fileId = service?.file?.id?.toString() ?? "";
  const { data: documentsData, isLoading: isLoadingDocuments } =
    useGetDocumentsQuery(
      {
        fileId,
        limit: "1000",
        offset: "0",
        sortBy: "original_filename",
      },
      {
        skip: !fileId || !isOpen,
      },
    );

  const { data: providers = [] } = useGetSupportedPayHistoryProvidersQuery(
    undefined,
    { skip: !isOpen },
  );
  const [extractPayHistory, { isLoading }] = useExtractPayHistoryMutation();

  const serviceParams = getServiceField(service, true);
  const { data: serviceDoiPage, isFetching: isLoadingDois } =
    useGetServiceDateOfInjuryQuery({ ...serviceParams }, { skip: !isOpen });
  const serviceDOIs: ServiceDateOfInjury[] = serviceDoiPage?.results ?? [];
  const fileClaimNumbers: ClaimNumber[] = service?.file?.claim_numbers ?? [];

  const documentOptions: OptionType[] = useMemo(() => {
    const docs: DocumentType[] = documentsData?.results ?? [];
    return docs
      .filter((doc) => {
        const name = (doc.original_filename || "").toLowerCase();
        return ACCEPTED_PAY_HISTORY_EXTENSIONS.some((ext) =>
          name.endsWith(ext),
        );
      })
      .map((doc) => ({
        label: doc.original_filename || `Document #${doc.id}`,
        value: doc.id,
      }));
  }, [documentsData]);

  const providerOptions: OptionType[] = providers.map((provider) => ({
    label: provider.display_name,
    value: provider.key,
  }));

  const doiOptions: OptionType[] = useMemo(
    () =>
      serviceDOIs.map((doi) => ({
        label: formatDoiDate(doi.date),
        value: doi.date_of_injury_id,
      })),
    [serviceDOIs],
  );

  const claimNumberOptions: OptionType[] = useMemo(
    () =>
      fileClaimNumbers
        .filter(
          (claim) =>
            selectedDoiId != null && claim.date_of_injury_id === selectedDoiId,
        )
        .map((claim) => ({
          label: claim.number,
          value: claim.id,
        })),
    [fileClaimNumbers, selectedDoiId],
  );

  const selectedDocOption =
    documentOptions.find((option) => option.value === selectedDocId) ?? null;
  const selectedDoiOption =
    doiOptions.find((option) => option.value === selectedDoiId) ?? null;
  const selectedClaimNumberOption =
    claimNumberOptions.find(
      (option) => option.value === selectedClaimNumberId,
    ) ?? null;

  useEffect(() => {
    if (isOpen) {
      if (preselectedDocumentId != null) {
        setSelectedDocId(preselectedDocumentId);
      }
    } else {
      setSelectedProvider(null);
      setSelectedDocId(null);
      setSelectedDoiId(null);
      setSelectedClaimNumberId(null);
    }
  }, [isOpen, preselectedDocumentId]);

  useEffect(() => {
    if (!isOpen || selectedDoiId != null || doiOptions.length !== 1) {
      return;
    }
    setSelectedDoiId(Number(doiOptions[0].value));
  }, [isOpen, doiOptions, selectedDoiId]);

  useEffect(() => {
    if (!isOpen) return;

    const matchingIds = claimNumberOptions.map((option) =>
      Number(option.value),
    );
    if (
      selectedClaimNumberId != null &&
      matchingIds.includes(selectedClaimNumberId)
    ) {
      return;
    }

    if (matchingIds.length === 1) {
      setSelectedClaimNumberId(matchingIds[0]);
      return;
    }

    setSelectedClaimNumberId(null);
  }, [isOpen, claimNumberOptions, selectedClaimNumberId]);

  const handleExtract = async () => {
    if (
      !selectedProvider ||
      selectedDocId == null ||
      selectedDoiId == null ||
      selectedClaimNumberId == null
    ) {
      return;
    }

    const docs: DocumentType[] = documentsData?.results ?? [];
    const selectedDoc = docs.find((d) => d.id === selectedDocId);
    if (!selectedDoc) {
      KaboodleToaster.error("Selected document not found.");
      return;
    }

    setIsExtracting(true);
    try {
      const filename = selectedDoc.original_filename || "pay_history.xlsx";

      const result = await extractPayHistory({
        insurer_key: String(selectedProvider.value),
        document: selectedDocId,
        date_of_injury: selectedDoiId,
        claim_number: selectedClaimNumberId,
        ...getServiceField(service, false),
      }).unwrap();

      if (result?.job_id) {
        KaboodleToaster.success(
          `Pay history extraction started for ${filename}. You can track progress in the Background Jobs drawer.`,
        );
      }
      onClose();
    } catch (error) {
      console.error("Extract Error:", error);
      const apiError = error as ApiError;
      KaboodleToaster.error(
        getErrorMessage(apiError, "Failed to start pay history extraction"),
      );
    } finally {
      setIsExtracting(false);
    }
  };

  const isLoadingState = isLoading || isExtracting;
  const canSubmit =
    Boolean(
      selectedProvider &&
      selectedDocId != null &&
      selectedDoiId != null &&
      selectedClaimNumberId != null,
    ) && !isLoadingState;

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={onClose}
      footer={
        <div className="flex jc-c">
          <Button onClick={handleExtract} disabled={!canSubmit}>
            {isLoadingState ? "Extracting..." : "Extract"}
          </Button>
          <Button onClick={onClose} disabled={isLoadingState}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="Extract Pay History Document">
        {preselectedDocumentId != null ? (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                fontSize: "12px",
                fontWeight: 600,
                marginBottom: "4px",
                display: "block",
                color: "#333",
              }}
            >
              Document
            </label>
            <div
              style={{
                padding: "8px 12px",
                backgroundColor: "#f8f9fa",
                border: "1px solid #ced4da",
                borderRadius: "4px",
                fontSize: "14px",
                fontWeight: 500,
                color: "#495057",
              }}
            >
              {selectedDocOption?.label ||
                (selectedDocId != null
                  ? `Document #${selectedDocId}`
                  : "Selected Document")}
            </div>
          </div>
        ) : (
          <Select
            name="document"
            label="Document from Documents Section"
            options={documentOptions}
            value={selectedDocOption}
            onChange={(option) => {
              const value = (option as OptionType | null)?.value;
              setSelectedDocId(value == null ? null : Number(value));
            }}
            isClearable={false}
            isSearchable={false}
            readOnly={isLoadingDocuments || !documentOptions.length}
            placeholder={
              isLoadingDocuments
                ? "Loading documents..."
                : documentOptions.length
                  ? "Select a document"
                  : "No pay history documents found in Documents section"
            }
          />
        )}
        <Select
          name="insurer_key"
          label="TPA/Client"
          options={providerOptions}
          value={selectedProvider}
          onChange={(option) =>
            setSelectedProvider((option as OptionType | null) ?? null)
          }
          isClearable={false}
          isSearchable={false}
          placeholder="Select a TPA/Client"
        />
        <Select
          name="date_of_injury"
          label="Date of Injury"
          options={doiOptions}
          value={selectedDoiOption}
          onChange={(option) => {
            const value = (option as OptionType | null)?.value;
            setSelectedDoiId(value == null ? null : Number(value));
          }}
          isClearable={false}
          isSearchable={false}
          readOnly={isLoadingDois || !doiOptions.length}
          placeholder={
            isLoadingDois
              ? "Loading dates of injury..."
              : doiOptions.length
                ? "Select a date of injury"
                : "No dates of injury on this service"
          }
        />
        <Select
          name="claim_number"
          label="Claim Number"
          options={claimNumberOptions}
          value={selectedClaimNumberOption}
          onChange={(option) => {
            const value = (option as OptionType | null)?.value;
            setSelectedClaimNumberId(value == null ? null : Number(value));
          }}
          isClearable={false}
          isSearchable={false}
          readOnly={!selectedDoiId || !claimNumberOptions.length}
          placeholder={
            !selectedDoiId
              ? "Select a date of injury first"
              : claimNumberOptions.length
                ? "Select a claim number"
                : "No claim numbers for this date of injury"
          }
        />
        <p
          style={{
            fontSize: "12px",
            color: "#6c757d",
            marginTop: "8px",
            marginBottom: "4px",
          }}
        >
          You may only upload pay history files for a single claim number at a
          time. Pay history files (.csv, .xlsx) must be uploaded through the
          Documents section.
        </p>
      </Card>
    </KaboodleModal>
  );
};

export default ExtractPayHistoryModal;
